<script setup lang="ts">
import { useResizableDialog } from '@/helpers/composables/useResizableDialog'
import type { WaitingRoomStatusMismatch } from '@/api/fileManagement'

interface Props {
  modelValue: boolean
  title: string
  mismatches: WaitingRoomStatusMismatch[]
}

withDefaults(defineProps<Props>(), {
  modelValue: false,
  title: 'Waiting Room - Incorrect status',
  mismatches: () => [],
})

interface Emit {
  (e: 'update:modelValue', value: boolean): void
}

const emit = defineEmits<Emit>()

const {
  dialogWidth,
  dialogHeight,
  dialogMinWidth,
  dialogMinHeight,
} = useResizableDialog({
  defaultWidth: 920,
  defaultHeight: 620,
  minWidth: 560,
  minHeight: 360,
})

const closeDialog = () => {
  emit('update:modelValue', false)
}

const formatStatusMismatch = (item: WaitingRoomStatusMismatch): string => {
  return `${item.item_label} ${item.identity} (#${item.item_id}): `
    + `Current status: ${item.current_status}. `
    + `Expected system status: ${item.expected_system_status}`
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="95vw"
    max-height="95vh"
    :width="dialogWidth"
    :height="dialogHeight"
    class="waiting-room-status-mismatch-dialog"
    @update:modelValue="(value: boolean) => emit('update:modelValue', value)"
  >
    <DialogResizeHandle
      v-model:dialogWidth="dialogWidth"
      v-model:dialogHeight="dialogHeight"
      :min-width="dialogMinWidth"
      :min-height="dialogMinHeight"
    />

    <VCard class="waiting-room-status-mismatch-card d-flex flex-column h-100">
      <VCardTitle class="vss-movable d-flex align-center">
        <MovableDialog />
        <span class="waiting-room-status-mismatch-title">
          {{ title }}
        </span>
        <VSpacer />
        <VBtn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="closeDialog"
        />
      </VCardTitle>

      <VDivider />

      <VCardText class="waiting-room-status-mismatch-content">
        <ul class="waiting-room-status-mismatch-list">
          <li
            v-for="(mismatch, index) in mismatches"
            :key="`${mismatch.target_type}-${mismatch.item_id}-${index}`"
            class="waiting-room-status-mismatch-list__item"
          >
            {{ formatStatusMismatch(mismatch) }}
          </li>
        </ul>
      </VCardText>

      <VDivider />

      <VCardActions class="justify-end">
        <VBtn
          class="mt-1"
          color="primary"
          @click="closeDialog"
        >
          Close
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
:deep(.waiting-room-status-mismatch-dialog .v-overlay__content) {
  max-width: 95vw;
  max-height: 95vh;
}

.waiting-room-status-mismatch-card {
  position: relative;
  overflow: hidden;
}

.waiting-room-status-mismatch-title {
  min-inline-size: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.waiting-room-status-mismatch-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.waiting-room-status-mismatch-list {
  display: grid;
  gap: 8px;
  margin: 0;
  padding-inline-start: 22px;
}

.waiting-room-status-mismatch-list__item {
  overflow-wrap: anywhere;
}
</style>
